import { IonIcon, IonModal } from "@ionic/react";
import { checkmark } from "ionicons/icons";
import { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  ModalOverlay,
  ModalCard,
  ModalTitle,
  ModalMeta,
  ModalActions,
  ModalPrimaryButton,
  ModalQuietButton,
  ModalOutlineButton,
  ModalError,
  ModalFields,
} from "../../../atoms/ModalKit";
import { TextField } from "../../../atoms/LoginTextField";
import { otpChallengeStatusResponse } from "../../../../types/user.types";

type VerificationChannel = "email" | "phone_number";

type ChannelStateMap<T> = Partial<Record<VerificationChannel, T>>;

export interface SignupContactVerificationSection {
  channel: VerificationChannel;
  value: string;
  otpStatus: otpChallengeStatusResponse | null;
  isVerified: boolean;
}

interface Props {
  isOpen: boolean;
  sections: SignupContactVerificationSection[];
  onVerify: (channel: VerificationChannel, code: string) => Promise<void>;
  onResend: (channel: VerificationChannel) => Promise<otpChallengeStatusResponse>;
  onCancel: () => Promise<void>;
}

const buildOtpStatusMap = (
  sections: SignupContactVerificationSection[]
): ChannelStateMap<otpChallengeStatusResponse | null> =>
  sections.reduce((previous, section) => {
    previous[section.channel] = section.otpStatus;
    return previous;
  }, {} as ChannelStateMap<otpChallengeStatusResponse | null>);

const SignupContactVerificationModal: FC<Props> = ({
  isOpen,
  sections,
  onVerify,
  onResend,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [verificationCodes, setVerificationCodes] = useState<ChannelStateMap<string>>({});
  const [otpStatusByChannel, setOtpStatusByChannel] = useState<ChannelStateMap<otpChallengeStatusResponse | null>>({});
  const [errorKeys, setErrorKeys] = useState<ChannelStateMap<string | null>>({});
  const [isVerifyingByChannel, setIsVerifyingByChannel] = useState<ChannelStateMap<boolean>>({});
  const [isResendingByChannel, setIsResendingByChannel] = useState<ChannelStateMap<boolean>>({});
  const [isCancelling, setIsCancelling] = useState(false);
  const [isVerificationCodeFieldUnlocked, setIsVerificationCodeFieldUnlocked] = useState<ChannelStateMap<boolean>>({});

  useEffect(() => {
    if (!isOpen) {
      setVerificationCodes({});
      setOtpStatusByChannel({});
      setErrorKeys({});
      setIsVerifyingByChannel({});
      setIsResendingByChannel({});
      setIsCancelling(false);
      setIsVerificationCodeFieldUnlocked({});
      return;
    }

    setVerificationCodes({});
    setOtpStatusByChannel(buildOtpStatusMap(sections));
    setErrorKeys({});
    setIsVerifyingByChannel({});
    setIsResendingByChannel({});
    setIsCancelling(false);
    setIsVerificationCodeFieldUnlocked({});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setOtpStatusByChannel((current) => ({
      ...current,
      ...buildOtpStatusMap(sections),
    }));
  }, [isOpen, sections]);

  useEffect(() => {
    if (!isOpen || sections.length === 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setOtpStatusByChannel((current) => {
        const nextState: ChannelStateMap<otpChallengeStatusResponse | null> = { ...current };

        (Object.keys(nextState) as VerificationChannel[]).forEach((channel) => {
          const currentStatus = nextState[channel];

          if (!currentStatus) {
            return;
          }

          nextState[channel] = {
            expires_in_seconds: Math.max(0, currentStatus.expires_in_seconds - 1),
            resend_available_in_seconds: Math.max(0, currentStatus.resend_available_in_seconds - 1),
          };
        });

        return nextState;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isOpen, sections.length]);

  const mapVerificationError = (channel: VerificationChannel, error: any): string => {
    const responseMessage = String(error?.response?.data?.message || "").toLowerCase();

    if (responseMessage.includes("invalid verification code")) {
      return "profile_verification_code_invalid";
    }

    if (
      responseMessage.includes("has expired") ||
      responseMessage.includes("verification code not found") ||
      responseMessage.includes("no longer valid")
    ) {
      return "profile_verification_code_expired";
    }

    if (responseMessage.includes("too many failed attempts")) {
      return "profile_verification_code_too_many_attempts";
    }

    if (error?.response?.status === 429 || responseMessage.includes("resend is not available yet")) {
      return "profile_verification_code_resend_rate_limited";
    }

    if (responseMessage.includes("email already exists")) {
      return "home_inscription_error_EMAIL_EXIST";
    }

    if (responseMessage.includes("phone number already exists")) {
      return "profile_change_phone_exists";
    }

    return channel === "email"
      ? "home_inscription_email_verification_error"
      : "home_inscription_phone_verification_error";
  };

  const getDescription = (channel: VerificationChannel, value: string) => {
    if (channel === "email") {
      return value
        ? t("profile_change_email_code_description", { value })
        : t("profile_change_email_code_description_fallback");
    }

    return value
      ? t("profile_change_phone_code_description", { value })
      : t("profile_change_phone_code_description_fallback");
  };

  const handleVerifySubmit = async (channel: VerificationChannel) => {
    const isBusy = isCancelling || isVerifyingByChannel[channel] || isResendingByChannel[channel];

    if (isBusy) {
      return;
    }

    if (!verificationCodes[channel]?.trim()) {
      setErrorKeys((current) => ({
        ...current,
        [channel]: "common_all_fields_required",
      }));
      return;
    }

    try {
      setIsVerifyingByChannel((current) => ({
        ...current,
        [channel]: true,
      }));
      setErrorKeys((current) => ({
        ...current,
        [channel]: null,
      }));
      await onVerify(channel, verificationCodes[channel]!.replace(/\D/g, "").slice(0, 6));
    } catch (error: any) {
      setErrorKeys((current) => ({
        ...current,
        [channel]: mapVerificationError(channel, error),
      }));
    } finally {
      setIsVerifyingByChannel((current) => ({
        ...current,
        [channel]: false,
      }));
    }
  };

  const handleResend = async (channel: VerificationChannel) => {
    const isBusy = isCancelling || isVerifyingByChannel[channel] || isResendingByChannel[channel];

    if (isBusy) {
      return;
    }

    try {
      setIsResendingByChannel((current) => ({
        ...current,
        [channel]: true,
      }));
      setErrorKeys((current) => ({
        ...current,
        [channel]: null,
      }));
      const nextStatus = await onResend(channel);
      setOtpStatusByChannel((current) => ({
        ...current,
        [channel]: nextStatus,
      }));
      setVerificationCodes((current) => ({
        ...current,
        [channel]: "",
      }));
    } catch (error: any) {
      setErrorKeys((current) => ({
        ...current,
        [channel]: mapVerificationError(channel, error),
      }));
    } finally {
      setIsResendingByChannel((current) => ({
        ...current,
        [channel]: false,
      }));
    }
  };

  const handleCancel = async () => {
    const hasActiveSectionWork =
      Object.values(isVerifyingByChannel).some(Boolean) ||
      Object.values(isResendingByChannel).some(Boolean);

    if (isCancelling || hasActiveSectionWork) {
      return;
    }

    try {
      setIsCancelling(true);
      setErrorKeys({});
      await onCancel();
    } finally {
      setIsCancelling(false);
    }
  };

  const description = useMemo(
    () => t("home_inscription_contact_verification_description"),
    [t]
  );

  return (
    <IonModal isOpen={isOpen} backdropDismiss={false}>
      <ModalOverlay>
        <VerificationCard>
          <ModalTitle>{t("profile_verification_code")}</ModalTitle>
          {/* <ModalDescription>{description}</ModalDescription> */}

          <Sections>
            {sections.map((section) => {
              const channel = section.channel;
              const otpStatus = otpStatusByChannel[channel] || null;
              const resendLabel = otpStatus?.resend_available_in_seconds
                ? t("profile_verification_code_resend_in", {
                    seconds: otpStatus.resend_available_in_seconds,
                  })
                : t("profile_verification_code_resend");
              const isBusy =
                isCancelling ||
                Boolean(isVerifyingByChannel[channel]) ||
                Boolean(isResendingByChannel[channel]);

              return (
                <Section key={channel} $isVerified={section.isVerified}>
                  <SectionHeader>
                    <SectionTitle>
                      {t(channel === "email" ? "home_inscription_email" : "home_inscription_phone")}
                    </SectionTitle>
                    {section.isVerified && (
                      <VerifiedBadge>
                        <VerifiedIcon icon={checkmark} />
                        <span>{t("home_inscription_contact_verified")}</span>
                      </VerifiedBadge>
                    )}
                  </SectionHeader>

                  <SectionDescription>{getDescription(channel, section.value)}</SectionDescription>

                  {!section.isVerified && (
                    <>
                      <ModalFields>
                        <TextField
                          value={verificationCodes[channel] || ""}
                          type="tel"
                          error={undefined}
                          onChange={(nextValue) => {
                            setVerificationCodes((current) => ({
                              ...current,
                              [channel]: nextValue.replace(/\D/g, "").slice(0, 6),
                            }));
                            setErrorKeys((current) => ({
                              ...current,
                              [channel]: null,
                            }));
                          }}
                          label={t("profile_verification_code")}
                          placeholder={t("profile_verification_code_placeholder")}
                          autoComplete="off"
                          inputProps={{
                            name: `signup-contact-verification-code-${channel}`,
                            readOnly: !isVerificationCodeFieldUnlocked[channel],
                            onFocus: () =>
                              setIsVerificationCodeFieldUnlocked((current) => ({
                                ...current,
                                [channel]: true,
                              })),
                            onPointerDown: () =>
                              setIsVerificationCodeFieldUnlocked((current) => ({
                                ...current,
                                [channel]: true,
                              })),
                            inputMode: "numeric",
                            autoCapitalize: "none",
                            autoCorrect: "off",
                            spellCheck: false,
                            maxLength: 6,
                            style: { WebkitTextSecurity: "none" } as CSSProperties,
                            "data-lpignore": "true",
                            "data-1p-ignore": "true",
                            "data-bwignore": "true",
                            "data-form-type": "other",
                          }}
                        />
                      </ModalFields>

                      <ModalMeta>
                        {otpStatus?.expires_in_seconds
                          ? t("profile_verification_code_expires_in", {
                              minutes: Math.ceil(otpStatus.expires_in_seconds / 60),
                            })
                          : t("profile_verification_code_expired_hint")}
                      </ModalMeta>

                      <SectionActions>
                        <ModalOutlineButton
                          type="button"
                          onClick={() => void handleResend(channel)}
                          disabled={Boolean(isResendingByChannel[channel]) || Boolean(otpStatus?.resend_available_in_seconds) || isCancelling}
                        >
                          {isResendingByChannel[channel] ? t("profile_action_processing") : resendLabel}
                        </ModalOutlineButton>

                        <ModalPrimaryButton
                          type="button"
                          onClick={() => void handleVerifySubmit(channel)}
                          disabled={isBusy}
                        >
                          {isVerifyingByChannel[channel]
                            ? t("profile_action_processing")
                            : t("profile_verification_code_verify")}
                        </ModalPrimaryButton>
                      </SectionActions>

                      {errorKeys[channel] && <ModalError>{t(errorKeys[channel]!)}</ModalError>}
                    </>
                  )}
                </Section>
              );
            })}
          </Sections>

          <ModalActions>
            <ModalQuietButton type="button" onClick={handleCancel} disabled={isCancelling}>
              {t(isCancelling ? "profile_action_processing" : "common_cancel")}
            </ModalQuietButton>
          </ModalActions>
        </VerificationCard>
      </ModalOverlay>
    </IonModal>
  );
};

/* Two verification sections side by side need more room than the kit default. */
const VerificationCard = styled(ModalCard)`
  max-width: 560px;
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Section = styled.div<{ $isVerified: boolean }>`
  /* Verified keeps its semantic success greens; the neutral state rides the
     theme tokens (soft surface panel + text-ink hairline). */
  border: 1px solid
    ${(props) =>
      props.$isVerified
        ? "#bbf7d0"
        : "rgba(var(--app-color-text-rgb, 4, 23, 35), 0.1)"};
  background: ${(props) =>
    props.$isVerified ? "#f0fdf4" : "var(--app-color-surface, #cdebf3)"};
  border-radius: 14px;
  padding: 14px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const SectionTitle = styled.div`
  color: var(--app-color-text, #041723);
  font-size: 15px;
  font-weight: 700;
`;

const SectionDescription = styled.p`
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.65);
  font-size: 13px;
  line-height: 1.5;
  margin: 10px 0 0;
`;

const VerifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #15803d;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
`;

const VerifiedIcon = styled(IonIcon)`
  color: #16a34a;
  font-size: 18px;
`;

const SectionActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
`;

export default SignupContactVerificationModal;